/**
 * The order the table is in, and what pressing a column head does to it.
 *
 * ── Three states per column, and the third is the way BACK ──────────────────────────────────
 * Press an inactive head: ascending by that column. Press the ACTIVE head: descending. Press it
 * again: back to the DEFAULT, which is the name ascending — the order the canon's own shot is in,
 * and the one this screen opens on with ĆWICZENIE inked and its caret up.
 *
 * The roster's heads have two states, because every ordering it offers has one useful direction
 * (worst first, oldest first). A catalogue is different: `Barki` first and `Łydki` first are both
 * things a trainer means, so a direction is worth the third press. And the third press is
 * load-bearing for the same reason the roster's second one is — the default belongs to no column,
 * so with no control of its own it would be reachable only by reloading the page.
 *
 * ── One collator per CALL, never one per comparison ─────────────────────────────────────────
 * `String.localeCompare` builds a collator for every pair it is handed: a 477-row sort is roughly
 * four thousand comparisons, and four thousand `Intl.Collator` constructions. One is built here and
 * closed over, which is the rule `entities/exercise-library` already states for its own sort.
 *
 * ── The collation is Polish, and the DEFAULT sensitivity is what makes it so ────────────────
 * `entities/exercise-library` sorts with `sensitivity: 'base'` on purpose — it is answering a
 * SEARCH, where `l` and `ł` must be the same letter so `zolnierskie` reaches `żołnierskie`. A
 * printed list is the opposite question: Polish has `ł` as its own letter after `l` and `ą` as its
 * own after `a`, and a trainer reading down a column expects to find them there. So this collator
 * takes the locale's real strength, and the two are different on purpose rather than by oversight.
 */

import type { ExerciseRow, ExerciseSortKeys } from './exerciseRow';

/** The five sortable columns. The keys are `ExerciseSortKeys`'s own. */
export type ExerciseSortColumn = keyof ExerciseSortKeys;

export const EXERCISE_SORT_COLUMNS: readonly ExerciseSortColumn[] = [
  'name',
  'type',
  'muscles',
  'equipment',
  'author',
];

/** The table's order: one column, one direction. There is no "unsorted" — see the default below. */
export interface ExerciseSort {
  readonly column: ExerciseSortColumn;
  readonly descending: boolean;
}

/**
 * Where the screen opens, and where a third press returns.
 *
 * **The default is a real ordering rather than an absence**, and the canon draws it that way: its
 * shot of the screen at rest has ĆWICZENIE inked with its caret up. A list of four hundred
 * movements is alphabetical or it is nothing, so "the order the catalogue arrived in" is not a state
 * a trainer ever wants — which is why it is not one of the states.
 */
export const DEFAULT_EXERCISE_SORT: ExerciseSort = { column: 'name', descending: false };

/**
 * What pressing `column` does to the order that is already in force.
 *
 * The whole cycle, as one function, so the head's `aria-sort` and the list's order can never
 * disagree about which press they are on.
 *
 * On ĆWICZENIE that is a two-state toggle, because the third state IS the default; on every other
 * column the third press hands the table back to the name. That asymmetry is the point rather than
 * an accident: the default belongs to no column, and with no control of its own it would be
 * reachable only by reloading the page.
 */
export function pressSortColumn(sort: ExerciseSort, column: ExerciseSortColumn): ExerciseSort {
  if (sort.column !== column) return { column, descending: false };
  if (!sort.descending) return { column, descending: true };
  return DEFAULT_EXERCISE_SORT;
}

/**
 * How the table's order should be SPOKEN, or `null` when it is in the default one.
 *
 * `aria-sort` on a `columnheader` is not announced by NVDA or by JAWS when the order changes — it
 * is read when the head itself is reached, which is not where a trainer who just pressed it is
 * standing. The screen's live region says how many movements are on the list, and that figure does
 * not move when the ORDER does, so a sort was announced nowhere at all.
 *
 * `null` for the default rather than „Ćwiczenie, rosnąco", because the default is what the screen
 * opens on: a live region that says it on arrival would be describing something nobody did.
 *
 * Keys out, no strings: `model/` may not import i18next. The caller resolves
 * `web:exerciseLibrary.columns.*` and the two direction words, which are the vocabulary the column
 * heads already print.
 */
export function exerciseSortAnnouncement(
  sort: ExerciseSort,
): { readonly column: ExerciseSortColumn; readonly descending: boolean } | null {
  const isDefault =
    sort.column === DEFAULT_EXERCISE_SORT.column &&
    sort.descending === DEFAULT_EXERCISE_SORT.descending;
  if (isDefault) return null;
  return { column: sort.column, descending: sort.descending };
}

/**
 * The rows in the order asked for.
 *
 * Ties break by NAME, always. Without it, sorting by Typ would leave every Siłowe movement in
 * whatever order the previous sort happened to put them in, and pressing the same head twice would
 * shuffle rows that compare equal — which reads as a list that cannot make up its mind.
 */
export function sortExerciseRows(
  rows: readonly ExerciseRow[],
  sort: ExerciseSort,
  locale: string,
): readonly ExerciseRow[] {
  const collator = new Intl.Collator(locale, { numeric: true });
  const direction = sort.descending ? -1 : 1;

  return [...rows].sort((left, right) => {
    const compared = collator.compare(left.sortKeys[sort.column], right.sortKeys[sort.column]);
    // The tie-break is ASCENDING whichever way the column is pointing: `Siłowe` descending still
    // reads A to Ż inside the group, because the direction is a statement about the column pressed
    // and not about every column at once.
    if (compared !== 0) return compared * direction;
    return collator.compare(left.sortKeys.name, right.sortKeys.name);
  });
}
